import type { Community } from '@/types/v2/community';
import axios from '@/lib/v2/axios';
import type { User } from '@/types/v2/user';

export async function getCommunities({ withRoles = false }: { withRoles?: boolean } = {}): Promise<{
  communities: Community[];
}> {
  const response = await axios.get(`/api/v2/communities?include=unitTypes${withRoles ? '&withRoles=true' : ''}`);
  return response.data;
}

export async function getCommunity(id: string): Promise<{ community: Community }> {
  const response = await axios.get(`/api/v2/communities/${id}?include=unitTypes`);
  return response.data;
}

export async function updateCommunityReservationPage(
  communityId: string,
  data: { amenity_reservations_description: string | null }
): Promise<Community> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/reservation-page`,
    data
  );
  return response.data;
}

export interface UpdateCommunityPreferencesData {
  amenity_reservations_description?: string | null;
  discussion_page_enabled?: boolean;
}

export async function updateCommunityPreferences(
  communityId: string,
  data: UpdateCommunityPreferencesData
): Promise<Community> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/preferences`,
    data
  );
  return response.data;
}

export async function getAvailableCommunityResidents(
  communityId: string,
  unitId: string,
): Promise<{ residents: User[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/residents?unitId=${unitId}`);
  return response.data;
}

export async function getCommunityManagers(communityId: string): Promise<{ managers: User[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/managers`);
  return response.data;
}

export async function getCommunityManager(communityId: string, userId: string): Promise<{ manager: User }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/managers/${userId}`);
  return response.data;
}

export async function getCommunityMembers(communityId: string): Promise<{ members: User[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/members`);
  return response.data;
}

export async function getCommunityMember(communityId: string, memberId: string): Promise<{ member: User }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/members/${memberId}`);
  return response.data;
}

export interface ActivityLog {
  id: string;
  log_name: string | null;
  description: string;
  properties: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  causer?: {
    id: string;
    name: string;
    email: string;
  };
  subject?: {
    id: string | null;
    type: string;
    name: string;
  };
}

export async function getCommunityActivityLogs(
  communityId: string,
  params: {
    per_page?: number;
    page?: number;
    sort?: string;
  } = {}
): Promise<{
  activities: ActivityLog[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_more: boolean;
    next_page: string | null;
    prev_page: string | null;
  };
}> {
  const response = await axios.get(`/api/v2/communities/${communityId}/activity`, { params });
  return response.data;
}

// Custom fields (resident profile fields defined per community)
export interface CommunityCustomField {
  id: string;
  community_id: string;
  name: string;
  key: string;
  visible_to_member: boolean;
  editable_by_member: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export async function getCommunityCustomFields(
  communityId: string
): Promise<{ data: CommunityCustomField[] }> {
  const response = await axios.get(`/api/v2/communities/${communityId}/custom-fields`);
  return response.data;
}

export async function createCommunityCustomField(
  communityId: string,
  data: { name: string; visible_to_member?: boolean; editable_by_member?: boolean }
): Promise<{ data: CommunityCustomField }> {
  const response = await axios.post(`/api/v2/communities/${communityId}/custom-fields`, data);
  return response.data;
}

export async function updateCommunityCustomField(
  communityId: string,
  customFieldId: string,
  data: { name?: string; visible_to_member?: boolean; editable_by_member?: boolean }
): Promise<{ data: CommunityCustomField }> {
  const response = await axios.patch(
    `/api/v2/communities/${communityId}/custom-fields/${customFieldId}`,
    data
  );
  return response.data;
}

export async function deleteCommunityCustomField(
  communityId: string,
  customFieldId: string
): Promise<void> {
  await axios.delete(`/api/v2/communities/${communityId}/custom-fields/${customFieldId}`);
}
